import React, { useState } from "react";
import Modal from "./Modal";
import { F } from "../data/aircraft";
import { STATS } from "../data/stats";
import { findPlace, iso, TODAY_ISO } from "../lib/engine";
import { createLead } from "../lib/api";
import { useSettings } from "../context/SettingsContext";

const acOptions = Object.keys(F).filter((k) => (STATS[k] || {}).n).sort((a, b) => F[a].n.localeCompare(F[b].n));

export default function RequestModal({ prefill = {}, onClose }) {
  const s = useSettings();
  const [form, setForm] = useState({
    from: prefill.from || "", to: prefill.to || "", date: prefill.date || "",
    ret: "", time: "Flexible", pax: prefill.pax || "", ac: prefill.ac || "",
    name: "", phone: "", email: "", note: prefill.med ? "Medical flight: " : "",
    consent: false, company: "",
  });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const cargo = prefill.mode === "cargo";

  const submit = async () => {
    const from = findPlace(form.from) || form.from;
    const to = findPlace(form.to) || form.to;
    const need = [];
    if (!from) need.push("where you're flying from");
    if (!to) need.push("where you're flying to");
    if (!form.date) need.push("the departure date");
    if (!form.pax) need.push(cargo ? "the cargo weight" : "the number of passengers");
    if (!form.name) need.push("your name");
    if (form.phone.replace(/\D/g, "").length < 8) need.push("a phone number we can reach you on");
    if (!form.consent) need.push("your consent to be contacted");
    if (need.length) { setErr("Please add " + need.join(", ") + "."); return; }
    setErr(""); setBusy(true);
    try {
      const res = await createLead({
        from_city: from, to_city: to, date: form.date, return_date: form.ret,
        time_pref: form.time, passengers: String(form.pax), aircraft_pref: form.ac ? F[form.ac].n : "",
        mode: prefill.mode || "", name: form.name, phone: form.phone, email: form.email,
        notes: form.note, consent: form.consent, company: form.company,
      });
      const fmt = (d) => (d ? new Date(d + "T00:00").toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }) : "");
      const msg = `New charter request (${res.reference})\n\nRoute: ${from} to ${to}\nDate: ${fmt(form.date)} (${form.time})${form.ret ? `\nReturn: ${fmt(form.ret)}` : ""}\n${cargo ? "Cargo: " + form.pax + " kg" : "Passengers: " + form.pax}\nAircraft preference: ${form.ac ? F[form.ac].n : "No preference"}\n${form.note ? `Notes: ${form.note}\n` : ""}Name: ${form.name}\nPhone: ${form.phone}${form.email ? `\nEmail: ${form.email}` : ""}`;
      setDone({ ref: res.reference, msg });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  const wa = (s.whatsapp || "").replace(/\D/g, "");
  const copy = async () => { try { await navigator.clipboard.writeText(done.msg); } catch (e) {} };

  return (
    <Modal title="Request a flight" onClose={onClose}>
      {!done ? (
        <>
          <div>
            <h2 style={{ margin: "0 0 4px", fontFamily: "var(--display)", fontWeight: 500, fontSize: 28 }}>Request a flight</h2>
            <p className="subt" style={{ margin: 0 }}>Tell us about your trip and we'll come back with available aircraft and a quote.</p>
          </div>
          <form className="req" onSubmit={(e) => { e.preventDefault(); submit(); }} noValidate>
            <input className="honey" tabIndex={-1} autoComplete="off" value={form.company} onChange={set("company")} aria-hidden="true" placeholder="Company" />
            <div className="row">
              <div className="fld"><label>From</label><input list="cityList" value={form.from} onChange={set("from")} required /></div>
              <div className="fld"><label>To</label><input list="cityList" value={form.to} onChange={set("to")} required /></div>
            </div>
            <div className="row">
              <div className="fld"><label>Departure date</label><input type="date" value={form.date} min={TODAY_ISO} onChange={set("date")} required /></div>
              <div className="fld"><label>Preferred time</label><select value={form.time} onChange={set("time")}><option>Flexible</option><option>Early morning</option><option>Morning</option><option>Afternoon</option><option>Evening</option></select></div>
            </div>
            <div className="row">
              <div className="fld"><label>{cargo ? "Cargo weight (kg)" : "Passengers"}</label><input type="number" min="1" max={cargo ? 30000 : 60} value={form.pax} onChange={set("pax")} required /></div>
              <div className="fld"><label>Return date <small>(optional)</small></label><input type="date" min={TODAY_ISO} value={form.ret} onChange={set("ret")} /></div>
            </div>
            <div className="fld"><label>Aircraft preference</label>
              <select value={form.ac} onChange={set("ac")}>
                <option value="">No preference, suggest the best option</option>
                {acOptions.map((k) => <option key={k} value={k}>{F[k].n}</option>)}
              </select>
            </div>
            <div className="row">
              <div className="fld"><label>Your name</label><input autoComplete="name" value={form.name} onChange={set("name")} required /></div>
              <div className="fld"><label>Phone / WhatsApp</label><input type="tel" placeholder="+91" value={form.phone} onChange={set("phone")} required /></div>
            </div>
            <div className="fld"><label>Email <small>(optional)</small></label><input type="email" value={form.email} onChange={set("email")} /></div>
            <div className="fld"><label>Anything else?</label><textarea placeholder="Occasion, luggage, catering, medical needs, helicopter landing at a venue..." value={form.note} onChange={set("note")} /></div>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13.5 }}>
              <input type="checkbox" checked={form.consent} onChange={set("consent")} style={{ width: 18, height: 18, marginTop: 2 }} />
              <span>I agree to be contacted about this request and accept the privacy policy.</span>
            </label>
            {err ? <div className="err" role="alert">{err}</div> : null}
            <div><button className="btn primary" type="submit" disabled={busy}>{busy ? "Sending\u2026" : "Send my request"}</button></div>
          </form>
        </>
      ) : (
        <div className="done">
          <h3 style={{ margin: 0, fontSize: 20 }}>Your request is in {"\u2713"}</h3>
          <div className="refbox">Reference number<br /><b>{done.ref}</b></div>
          <p className="subt" style={{ margin: 0 }}>We've received your request and will reply with available aircraft and a quote.{s.email ? <> You can also email us at <b>{s.email}</b>.</> : null}</p>
          <pre>{done.msg}</pre>
          <div className="acts">
            {wa ? <a className="wa" href={`https://wa.me/${wa}?text=${encodeURIComponent(done.msg)}`} target="_blank" rel="noopener noreferrer">Continue on WhatsApp</a> : null}
            <button className="btn" type="button" onClick={copy}>Copy request</button>
            <button className="btn" type="button" onClick={onClose}>Done</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
